import { useCallback, useState } from 'react';
import { uaePassService } from '../services/uaePassService';
import {
    UAEPassAuthResult,
    UAEPassUser
} from '../types';

export interface UseUAEPassReturn {
    // State
    isLoading: boolean;
    user: UAEPassUser | null;
    error: string | null;

    // Actions
    authenticate: () => Promise<UAEPassAuthResult | null>;
    clearError: () => void;
    reset: () => void;
}

/**
 * Custom hook for UAE Pass authentication
 * Provides a complete interface for UAE Pass integration
 */
export const useUAEPass = (): UseUAEPassReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<UAEPassUser | null>(null);
    const [error, setError] = useState<string | null>(null);


    /**
     * Initiate UAE Pass authentication
     * Optimized flow that automatically handles app detection and ACR values
     */
    const authenticate = useCallback(async (): Promise<UAEPassAuthResult | null> => {
        try {
            setIsLoading(true);
            setError(null);

            const result: UAEPassAuthResult = await uaePassService.authenticate();

            if (result.success) {
                if (result.user) {
                    setUser(result.user);
                }
                // Return the result so component can handle authUrl and webview
                return result;
            } else {
                setError(result.error || 'Authentication failed');
                return result;
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
            setError(errorMessage);
            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    }, []);



    /**
     * Clear current error
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Reset all state
     */
    const reset = useCallback(() => {
        setIsLoading(false);
        setUser(null);
        setError(null);
    }, []);
    return {
        // State
        isLoading,
        user,
        error,

        // Actions
        authenticate,
        clearError,
        reset
    };
};

export default useUAEPass;